import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnInit,
  Output,
  inject,
  DestroyRef,
  input,
} from '@angular/core';
import {
  EmailDocumentsGQL,
  DocumentSource,
  UserGQL,
  EmailDocumentsMutation,
} from '@app/generated/graphql';
import {
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { ToastService } from '@app/core/services/toast/toast.service';
import { of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/browser';
import { EmailAdressesHelper } from '../../helpers/emailAddresses.helper';
import { ITaskDocumentType } from '@app/account/shipments/documents-tab/documents-tab.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { Ag1ChipComponent } from '../ag1-chip/ag1-chip.component';
import { Ag1ChipValueAccessorDirective } from '../../directives/ag1-chip-value-accessor.directive';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Ag1TagComponent } from '../ag1-tag/ag1-tag.component';
import { Ag1BannerComponent } from '../ag1-banner/ag1-banner.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export type EmailUpdateData = {
  recipients: number;
  subjectChange: boolean;
  contentChange: boolean;
};

interface EmailDocumentsResponse {
  emailDocuments: {
    errors?: string;
    message: string;
  };
}

@Component({
  selector: 'ag1-email-modal',
  templateUrl: './ag1-email-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AngularSvgIconModule,
    FormsModule,
    ReactiveFormsModule,
    Ag1ChipComponent,
    Ag1ChipValueAccessorDirective,
    Bind,
    Button,
    Ag1TagComponent,
    Ag1BannerComponent,
  ],
})
export class Ag1EmailModalComponent
  extends EmailAdressesHelper
  implements OnInit
{
  private _destroyRef = inject(DestroyRef);
  private apollo = inject(Apollo);
  private toastService = inject(ToastService);
  private formBuilder = inject(UntypedFormBuilder);
  emailDocumentsGQL = inject(EmailDocumentsGQL);
  private userGQL = inject(UserGQL);

  readonly subject = input.required<string>();
  readonly content = input.required<string>();
  readonly attachments = input.required<object[]>();
  readonly pastEmailRecipients = input.required<string[]>();
  readonly backButton = input(false);

  @Output()
  emailerDestroyEvent: EventEmitter<{ id: number; updates: EmailUpdateData }> =
    new EventEmitter<{ id: number; updates: EmailUpdateData }>();
  @Output()
  backButtonDestroyEvent: EventEmitter<void> = new EventEmitter<void>();

  returnStatus = {
    SUCCESS: 1,
    ABORTED: 2,
    FAILED: 3,
  };

  // detailed view
  emails: string[] = [];
  docIds: number[] = [];

  emailForm: FormGroup;
  allEmailAddresses: string[] = [];

  // Focus
  toEmailAddressFocus: boolean = true;
  ccEmailAddressFocus: boolean = false;
  subjectFocus: boolean = false;
  textareaFocus: boolean = false;
  attachmentFocus: boolean = false;

  isCCFieldExpanded: boolean = false;

  constructor() {
    super();
    this.emailForm = this.formBuilder.group({
      toEmailAddresses: new FormControl<string[] | null>(null, [
        Validators.required,
      ]),
      ccEmailAddresses: new FormControl<string[] | null>(null),
      emailSubject: new FormControl<string | null>(null, [Validators.required]),
      emailBody: new FormControl<string | null>(null, [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.initializeFormData();
    this.storeAllEmailAddresses();
  }

  initializeFormData(): void {
    this.setDefaultEmailAddress();
    this.emailForm.get('emailSubject').setValue(this.subject());
    this.emailForm.get('emailBody').setValue(this.content());
    this.docIds = this.attachments()?.map((attachment) => {
      return attachment['id'];
    });
  }

  storeAllEmailAddresses(): void {
    this.allEmailAddresses = [
      ...new Set([...this.pastEmailRecipients(), ...this.localEmailAddresses]),
    ];
  }

  setDefaultEmailAddress(): void {
    this.userGQL
      .fetch()
      .pipe(
        map((res): string | null => res.data.myUser?.email),
        tap((userEmail: string | null) => {
          if (userEmail) {
            this.emailForm.get('ccEmailAddresses').setValue([userEmail]);
          }
        }),
        takeUntilDestroyed(this._destroyRef),
      )
      .subscribe();
  }

  closeComponent(): void {
    this.emailerDestroyEvent.emit();
  }

  backButtonPress(): void {
    this.backButtonDestroyEvent.emit();
  }

  updateEmailList(event: string[]): void {
    const currentList = this.localEmailAddresses;
    const additionalList = event.filter(
      (item) => !this.pastEmailRecipients().includes(item),
    );
    this.localEmailAddresses = [
      ...new Set([...currentList, ...additionalList]),
    ];
  }

  removeAttachment(event: any): void {
    const index = this.attachments().findIndex((obj) => obj['id'] === event.id);

    if (index !== -1) {
      this.attachments().splice(index, 1);
    }
    this.docIds = this.attachments().map((attachment) => {
      return attachment['id'];
    });
  }

  sendEmail(): void {
    const toEmails: string[] = this.emailForm.value.toEmailAddresses || [];
    const ccEmails: string[] = this.emailForm.value.ccEmailAddresses || [];

    const updates: EmailUpdateData = {
      recipients: toEmails.length + ccEmails.length,
      subjectChange: this.emailForm.value.emailSubject !== this.subject(),
      contentChange: this.emailForm.value.emailBody !== this.content(),
    };
    const documentsSource = this.attachments().reduce(
      (acc: DocumentSource[], doc: ITaskDocumentType) => {
        if (this.docIds.includes(doc.id)) {
          acc.push({ id: String(doc.id), source: doc.source });
        }
        return acc;
      },
      [],
    );

    const ccRecipients: string[] = ccEmails;

    this.apollo
      .mutate<EmailDocumentsMutation>({
        mutation: this.emailDocumentsGQL.document,
        variables: {
          attributes: {
            documents: documentsSource,
            recipients: toEmails,
            cc: ccRecipients,
            subject: this.emailForm.value.emailSubject,
            body: this.emailForm.value.emailBody,
          },
        },
      })
      .pipe(
        tap((res: { data: EmailDocumentsResponse }) => {
          const response = res.data?.emailDocuments;
          if (response.errors) {
            this.toastService.showErrorToast(
              `Email could not be sent - ${response.errors}`,
            );
            Sentry.captureException(response.errors);
          } else if (response) {
            // Success message
            this.toastService.showSuccessToast(response.message);
            this.emailerDestroyEvent.emit({
              id: this.returnStatus['SUCCESS'],
              updates: updates,
            });
          }
        }),
        catchError((err) => {
          Sentry.captureException(err);
          console.error(err);
          this.toastService.showErrorToast('Failed to send email');
          return of(null);
        }),
      )
      .subscribe();
  }

  validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  }

  changeFocus(focusedField: string): void {
    this.toEmailAddressFocus = false;
    this.ccEmailAddressFocus = false;
    this.subjectFocus = false;
    this.textareaFocus = false;
    this.attachmentFocus = false;

    switch (focusedField) {
      case 'toEmailAddress':
        this.toEmailAddressFocus = true;
        break;
      case 'ccEmailAddress':
        this.ccEmailAddressFocus = true;
        break;
      case 'subject':
        this.subjectFocus = true;
        break;
      case 'textarea':
        this.textareaFocus = true;
        break;
      case 'attachments':
        this.attachmentFocus = true;
        break;
    }
  }

  expandCcField(): void {
    this.isCCFieldExpanded = true;
  }

  checkForFinalInvoice(type: string): string {
    return type === 'Tax Invoice' ? 'Final Invoice' : type;
  }
}
